import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { customInstance } from "@/lib/axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

interface PlatformOption {
  id: string;
  label: string;
}

export function AdminMarketplaceSourceDealersPage() {
  const [platform, setPlatform] = useState<string>("all");
  const [isExporting, setIsExporting] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["marketplace-source-dealer-platforms"],
    queryFn: async () => {
      const { data: res } = await customInstance.get<{ platforms: PlatformOption[] }>(
        "/api/reports/marketplace-source-dealers/platforms/"
      );
      return res;
    },
  });

  const platforms = data?.platforms ?? [{ id: "all", label: "All platforms" }];

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const response = await customInstance.post(
        "/api/reports/marketplace-source-dealers/export/",
        { platform },
        { responseType: "blob" }
      );
      const platSlug = platform && platform !== "all" ? platform : "all";
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `marketplace_source_dealers_${platSlug}_${new Date().toISOString().split("T")[0]}.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Export downloaded");
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } } };
      toast.error(err.response?.data?.error || "Failed to export");
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl space-y-6 p-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Marketplace source dealers</h1>
        <p className="mt-2 text-sm text-gray-600">
          Export selling dealers captured from marketplace listing embeds (e.g. Fastline). All-time by
          embed created date. Excludes listings without a dealer name.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Export Excel</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="platform">Marketplace platform</Label>
            <select
              id="platform"
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              value={platform}
              disabled={isLoading}
              onChange={(e) => setPlatform(e.target.value)}
            >
              {platforms.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.label}
                </option>
              ))}
            </select>
          </div>

          <p className="text-xs text-gray-500">
            Date range: all time (application created at embed). File includes three sheets: dealer
            summary (one row per dealer name), application detail, and source dealer records (per
            database row, with stage counts).
          </p>

          <Button
            type="button"
            className="bg-blue-600 text-white hover:bg-blue-700"
            disabled={isExporting || isLoading}
            onClick={() => void handleExport()}
          >
            {isExporting ? "Exporting…" : "Download Excel"}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
